import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { StripeCardElement } from "@stripe/stripe-js";
import AppBox from "@/components/AppBox";
import AppButton from "@/components/AppButton";

// Props interface
interface AppCheckoutFormProps {
  clientSecret: string;
  isOnSubscription: boolean;
  onSubscribe: (paymentMethodId: string) => Promise<void>;
  onSwitchToInfo: () => void;
}

// Component
const AppCheckoutForm: React.FC<AppCheckoutFormProps> = ({
  clientSecret,
  onSwitchToInfo,
  isOnSubscription,
  onSubscribe,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const { me } = useSelector((state: any) => state.auth); // Replace `any` with a proper auth state type
  const cardElement = useRef<StripeCardElement | null>(null);

  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Initialize Stripe form
  useEffect(() => {
    if (!cardElement.current && elements) {
      createStripeForm();
    }
  }, [elements]);

  const createStripeForm = () => {
    if (!elements) return;

    const card = elements.create("card", {
      style: {
        base: {
          color: "#32325d",
          lineHeight: "18px",
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: "antialiased",
          fontSize: "16px",
          "::placeholder": {
            color: "#aab7c4",
          },
        },
        invalid: {
          color: "#fa755a",
          iconColor: "#fa755a",
        },
      },
      hidePostalCode: true,
    });

    card.mount("#card-element");
    cardElement.current = card;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!cardElement.current) {
      console.error("Card Element not mounted properly.");
    }

    if (!stripe || !elements || !cardElement.current) {
      console.error("Stripe.js is not yet initialized");
      return;
    }

    setMessage("");
    setIsLoading(true);
    
    if (!clientSecret) {
      console.error("Missing clientSecret");
      setIsLoading(false);
      return;
    }    

    const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement.current,
        billing_details: {
          email: me?.email,
        },
      },
    });
    
    if (error) {
      setMessage(error.message || "An error occurred during payment setup.");
    } else if (setupIntent?.payment_method) {
      await onSubscribe(typeof setupIntent.payment_method === "string"
        ? setupIntent.payment_method
        : setupIntent.payment_method?.id
      );
    }

    setIsLoading(false);
  };

  return (
    <form>
      <div id="card-element" className="mb-2 border border-gray-300 px-2 pt-[11px] h-[42px] w-[420px] rounded"></div>
      {message && <div className="text-red-500 mb-4">{message}</div>}

      <AppBox width={420} justify="between" gap={2}>
        <AppBox width={160}>
          <AppButton onClick={handleSubmit} isLoading={isLoading} id="submit">
            {isOnSubscription ? "Confirm" : "Complete Signup"}
          </AppButton>
        </AppBox>
        <AppBox>
          {isOnSubscription && (
            <AppButton onClick={onSwitchToInfo} variant="outlined">
              Cancel
            </AppButton>
          )}
        </AppBox>
      </AppBox>
    </form>
  );
};

export default AppCheckoutForm;